import { Injectable } from '@angular/core';
import { LocalDbService } from '../local-db-service';
import { Order } from '../../entities/order';
import { BoxType } from '../../enums/box-type';
import { OrderState } from 'src/app/proxy/orders/enums';
import { OrderVisibility } from '../../models/order-visibility';

@Injectable({ providedIn: 'root' })
export class OrderRepository {
  private get table() {
    return this.db.ordersTable;
  }

  constructor(private readonly db: LocalDbService) {}

  async get(boxType: BoxType, state: OrderState, userId: number,
    visibility: OrderVisibility,
  ): Promise<Order[]> {

    if (userId == null) {
      return [];
    }

    let rows: Order[] = [];
    if (visibility === 'all') {
      rows = await this.table.toArray();
    }
    if (visibility === 'approver') {
      rows = await this.table.where('approverId').equals(userId).toArray();
    }
    if (visibility === 'requester') {
      rows = await this.table.where('requesterId').equals(userId).toArray();
    }
    if (visibility === 'originWarehouse') {
      const warehouses = await this.db.warehouseTable.where('warehouseManagerId').equals(userId).toArray();
      const warehouseIds = warehouses.map(w => w.id);
      rows = await this.table.where('originWarehouseId').anyOf(warehouseIds).toArray();
    }
    if (visibility === 'destinationWarehouse') {
      const warehouses = await this.db.warehouseTable.where('warehouseManagerId').equals(userId).toArray();
      const warehouseIds = warehouses.map(w => w.id);
      rows = await this.table.where('destinationWarehouseId').anyOf(warehouseIds).toArray();
    }
    if (boxType === BoxType.Local) {
      rows = await this.table.toCollection()
        .and(o => o.requesterId === o.approverId && o.approverId === userId).toArray();
    }

    if (state !== undefined) {
      rows = rows.filter(x => x.orderState === state);
    }
    const orders = rows.map(row => new Order(row));
    const filtredOrders = this.filterOrders(orders, 30, state);
    return filtredOrders.sort((a, b) => {
      const dateA = a.creationDate ? new Date(a.creationDate).getTime() : 0; // NOSONAR - TODO implement sort util
      const dateB = b.creationDate ? new Date(b.creationDate).getTime() : 0;
      return dateB - dateA;
    });
  }

  private filterOrders(orders: Order[], daysToHistory: number, stateFilter?: OrderState,): Order[] {
    const now = new Date();
    if (stateFilter) {
      if (stateFilter === OrderState.Rejected || stateFilter === OrderState.Received) {
        return orders.filter(x => 
          x.orderState === stateFilter && 
          x.lastModificationDate != null &&
          new Date(new Date(x.lastModificationDate).getTime() + daysToHistory * 86400000) > now
        );
      } else {
        return orders.filter(x => x.orderState === stateFilter);
      }
    } else {
      return orders.filter(x => {
        if (x.orderState === OrderState.Rejected || x.orderState === OrderState.Received) {
          return x.lastModificationDate != null && 
            new Date(new Date(x.lastModificationDate).getTime() + daysToHistory * 86400000) > now;
        }
        return x.orderState === OrderState.New || x.orderState === OrderState.Approved || x.orderState === OrderState.Delivered;
      });
    }
  }

  async getAll(): Promise<Order[]> {
    const rows = await this.table.toArray();
    return rows.map(row => new Order(row));
  }

  async getById(localId: string): Promise<Order | undefined> {
    const row = await this.table.get(localId);
    return row ? new Order(row) : undefined;
  }

  async getByRemoteId(remoteId: number): Promise<Order | undefined> {
    const row = await this.table.where('id').equals(remoteId).first();
    return row ? new Order(row) : undefined;
  }

  async getByIdentifier(id: string): Promise<Order | undefined> {
    const localOrder = await this.getById(id);
    if (localOrder) {
      return localOrder;
    }

    const remoteId = Number(id);
    if (Number.isNaN(remoteId)) {
      return undefined;
    }

    return this.getByRemoteId(remoteId);
  }

  async getPendingSendCount(): Promise<number> {
    return this.table.filter(order => order.pendingSend === true).count();
  }

  async getPendingSendOrders(): Promise<Order[]> {
    const rows = await this.table.filter(order => order.pendingSend === true).toArray();
    return rows.map(row => new Order(row));
  }

  async hasPendingSendOrders(): Promise<boolean> {
    return (await this.getPendingSendCount()) > 0;
  }

  upsert(order: Order): Promise<string> {
    return this.table.put(order);
  }

  async upsertRemote(order: Order): Promise<string> {
    const existingOrders = order.id != null
      ? await this.table.where('id').equals(order.id).toArray()
      : [];

    const existingOrder = existingOrders.find(existing => existing.localId === order.localId)
      ?? existingOrders[0];

    const orderToSave = new Order({
      ...order,
      localId: order.localId ?? existingOrder?.localId,
      pendingSend: order.pendingSend ?? existingOrder?.pendingSend ?? false,
    });

    const savedLocalId = await this.table.put(orderToSave);
    const duplicateLocalIds = existingOrders
      .map(existing => existing.localId)
      .filter(localId => localId !== orderToSave.localId);

    if (duplicateLocalIds.length > 0) {
      await this.table.bulkDelete(duplicateLocalIds);
    }

    return savedLocalId;
  }

  async upsertRemoteBulk(orders: Order[]): Promise<string> {
    const remoteIds = orders
      .map(order => order.id)
      .filter((id): id is number => id != null);

    const existingOrders = remoteIds.length > 0
      ? await this.table.where('id').anyOf(remoteIds).toArray()
      : [];

    const existingOrdersByRemoteId = new Map<number, Order[]>();
    for (const existingOrder of existingOrders) {
      const remoteId = existingOrder.id;
      if (remoteId == null) {
        continue;
      }

      const remoteOrders = existingOrdersByRemoteId.get(remoteId) ?? [];
      remoteOrders.push(existingOrder);
      existingOrdersByRemoteId.set(remoteId, remoteOrders);
    }

    const keptLocalIds = new Set<string>();
    const ordersToSave = orders.map(order => {
      const remoteOrders = order.id != null
        ? existingOrdersByRemoteId.get(order.id) ?? []
        : [];
      const existingOrder = remoteOrders.find(existing => existing.localId === order.localId)
        ?? remoteOrders[0];

      const localId = order.localId ?? existingOrder?.localId;
      if (localId) {
        keptLocalIds.add(localId);
      }

      return new Order({
        ...order,
        localId,
        pendingSend: order.pendingSend ?? existingOrder?.pendingSend ?? false,
      });
    });

    const savedLocalId = await this.table.bulkPut(ordersToSave);
    const duplicateLocalIds = existingOrders
      .map(existing => existing.localId)
      .filter(localId => !keptLocalIds.has(localId));

    if (duplicateLocalIds.length > 0) {
      await this.table.bulkDelete(duplicateLocalIds);
    }

    return savedLocalId;
  }

  delete(localId: string): Promise<void> {
    return this.table.delete(localId);
  }

  clear(): Promise<void> {
    return this.table.clear();
  }
}
